import type { SourceKind } from '@/components/audio/audioUi';

import type { TranscriptExample } from './TranscriptMock';

export type LandingStep = { title: string; body: string };
export type LandingFaq = { question: string; answer: string };

/** Three reasons this page's kind of recording works well here. */
export type LandingDetails = {
  title: string;
  /** Muted second half of the headline. */
  tail?: string;
  items: [LandingStep, LandingStep, LandingStep];
};

/**
 * Everything that differs between the transcription landing pages. All text
 * is an English key, translated on render; {{placeholders}} take `vars`
 * (a language page passes the language name) plus {{languages}}, the number
 * of languages the app offers.
 */
export type LandingCopy = {
  /** Analytics name of the page. */
  id: string;
  path: string;
  /** Title tag without the brand: Seo adds " | Easy-Peasy.AI". */
  seoTitle: string;
  seoDescription: string;
  /** Short title for the social card image. */
  ogTitle: string;
  eyebrow: string;
  title: string;
  /** Second half of the headline, in the accent colour. */
  titleTail?: string;
  lead: string;
  example: TranscriptExample;
  steps: [LandingStep, LandingStep, LandingStep];
  details: LandingDetails;
  /** Page-specific questions; the shared ones follow. */
  faqs: LandingFaq[];
  vars?: Record<string, string>;
  /** What the hero offers: an upload box (default) or the note taker's recorder. */
  cta?: 'upload' | 'record';
  /** Link pages: the hero leads with this link field (label is an English key). */
  link?: { label: string; placeholder: string; source: SourceKind };
};

/** Format and source tools, linked from every page (English labels are keys). */
export const TOOL_LINKS: { path: string; label: string }[] = [
  { path: '/ai-transcription/audio-to-text-converter', label: 'Audio to text' },
  { path: '/ai-transcription/video-to-text-converter', label: 'Video to text' },
  { path: '/ai-transcription/mp3-to-text-converter', label: 'MP3 to text' },
  { path: '/ai-transcription/mp4-to-text-converter', label: 'MP4 to text' },
  { path: '/ai-transcription/m4a-to-text-converter', label: 'M4A to text' },
  { path: '/ai-transcription/wav-to-text-converter', label: 'WAV to text' },
  { path: '/ai-transcription/youtube', label: 'YouTube transcript' },
  {
    path: '/ai-transcription/youtube-summarizer',
    label: 'YouTube summarizer',
  },
  {
    path: '/ai-transcription/tiktok-transcript-generator',
    label: 'TikTok transcript',
  },
  {
    path: '/ai-transcription/instagram-transcript-generator',
    label: 'Instagram transcript',
  },
  { path: '/ai-transcription/voice-memo-to-text', label: 'Voice memo to text' },
];

export type UseCaseLink = {
  path: string;
  title: string;
  body: string;
  /** What the app writes for this kind of recording (AI Content names). */
  outputs: string[];
  icon: 'meeting' | 'zoom' | 'interview' | 'podcast' | 'lecture' | 'notetaker';
};

/** Pages for one kind of recording, linked from every page. */
export const USE_CASE_LINKS: UseCaseLink[] = [
  {
    path: '/ai-transcription/meeting',
    title: 'Meetings',
    body: 'Who said what, the decisions made and who does what next.',
    outputs: ['Summary', 'Action Items', 'Questions'],
    icon: 'meeting',
  },
  {
    path: '/ai-transcription/zoom',
    title: 'Zoom, Meet and Teams recordings',
    body: 'Upload the recording of a call and get the transcript with every speaker named.',
    outputs: ['Summary', 'Action Items', 'Topics and Bullets'],
    icon: 'zoom',
  },
  {
    path: '/ai-transcription/interview',
    title: 'Interviews',
    body: 'Every answer labelled and timed, so quotes are easy to find and check.',
    outputs: ['Summary', 'Questions', 'Article'],
    icon: 'interview',
  },
  {
    path: '/ai-transcription/podcast',
    title: 'Podcasts',
    body: 'A full episode transcript with show notes and chapters ready to publish.',
    outputs: ['Show Notes', 'Timestamped Overview', 'Article'],
    icon: 'podcast',
  },
  {
    path: '/ai-transcription/lecture',
    title: 'Lectures and classes',
    body: 'Searchable notes from every class, with the key points pulled out for revision.',
    outputs: ['Summary', 'Topics and Bullets', 'Keywords'],
    icon: 'lecture',
  },
  {
    path: '/ai-note-taker',
    title: 'AI Note Taker',
    body: 'Record a meeting, a class or a call in your browser and get the notes when you stop.',
    outputs: ['Summary', 'Action Items', 'Linkedin Post'],
    icon: 'notetaker',
  },
];
